import { useMemo, useState } from 'react';
import { Lead } from '../types/lead';
import { LeadCard } from './LeadCard';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Filter, CheckCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';

interface RequestedLeadsProps {
  leads: Lead[];
  onViewDetails: (leadId: string) => void;
  onAcceptLead?: (leadId: string) => void;
  onDeclineLead?: (leadId: string) => void;
  canRespondToJobs?: boolean;
  canAcceptJobs?: boolean;
}

export function RequestedLeads({
  leads,
  onViewDetails,
  onAcceptLead,
  onDeclineLead,
  canRespondToJobs = false,
  canAcceptJobs = false,
}: RequestedLeadsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('recent');

  // ✅ Get unique categories from leads
  const uniqueCategories = useMemo(() => {
    const categories = leads
      .map((lead) => lead.category?.trim().toLowerCase())
      .filter(Boolean); // remove undefined/null
    const unique = Array.from(new Set(categories)); // remove duplicates
    return ['all', ...unique];
  }, [leads]);

  // ✅ Filter and sort
  const filteredAndSortedLeads = leads
    .filter((lead) => {
      const matchesSearch =
        lead.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.location.city.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        categoryFilter === 'all' || lead.category === categoryFilter;

      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return (
          new Date(b.requestedDate || b.postedDate).getTime() -
          new Date(a.requestedDate || a.postedDate).getTime()
        );
      } else if (sortBy === 'budget-high') {
        return Number(b.budget) - Number(a.budget);
      } else if (sortBy === 'budget-low') {
        return Number(a.budget) - Number(b.budget);
      }
      return 0;
    });

  return (
    <div className="lead-management-content">
      <div className="">
        {/* Info Alert */}
        {leads.length > 0 && (
          <Alert className="mb-6 border-blue-200 bg-blue-50 flex">
            <CheckCircle className="size-4 text-blue-600" />
            <AlertDescription className="text-blue-900 flex">
              You have {leads.length} active lead request
              {leads.length !== 1 ? 's' : ''}. Homeowners typically respond within 24-48 hours.
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="size-5" />
            <div>Filter Requested Leads</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="app-search-field">
              <Search className="app-field-icon" />
              <Input
                placeholder="Search requested leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* ✅ Dynamic Categories */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {uniqueCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="budget-high">Highest Budget</SelectItem>
                <SelectItem value="budget-low">Lowest Budget</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Filters Display */}
          {(searchQuery || categoryFilter !== 'all') && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <span className="text-muted-foreground">Active filters:</span>
              {searchQuery && (
                <Badge variant="secondary">Search: {searchQuery}</Badge>
              )}
              {categoryFilter !== 'all' && (
                <Badge variant="secondary">Category: {categoryFilter}</Badge>
              )}
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="mb-4">
          <p className="text-muted-foreground">
            Showing {filteredAndSortedLeads.length} of {leads.length} requested leads
          </p>
        </div>

        {(!canRespondToJobs || !canAcceptJobs) && (
          <Alert className="mb-4 border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-900">
              Accepting requires `RESPOND_JOB` and `MANAGE_BILLING`; declining requires `RESPOND_JOB`.
            </AlertDescription>
          </Alert>
        )}

        {/* Leads Grid */}
        {filteredAndSortedLeads.length > 0 ? (
          <div className="grid grid-cols-1 gap-6">
            {filteredAndSortedLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onViewDetails={onViewDetails}
                onAcceptLead={canAcceptJobs ? onAcceptLead : undefined}
                onDeclineLead={canRespondToJobs ? onDeclineLead : undefined}
                showActionButtons={true}
                isRequestedLead={true}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            {leads.length === 0 ? (
              <>
                <p className="text-muted-foreground mb-2">
                  You haven't requested any leads yet.
                </p>
                <p className="text-muted-foreground">
                  Browse available leads in the Leads Pro section to get started.
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">
                No requested leads found matching your criteria. Try adjusting your filters.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
